import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell
} from "recharts";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  TrendingUp, TrendingDown, Package, DollarSign, Truck, BarChart3,
  Download, Printer, RefreshCw, ChevronDown, ChevronUp, Search,
  FileDown, FileSpreadsheet, ArrowUpDown, GitCompare, Filter
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (val) => {
  if (!val) return "₹0";
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)}Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(2)}L`;
  if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
  return `₹${Math.round(val).toLocaleString("en-IN")}`;
};

const toDDMMYY = (date) => {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = String(date.getFullYear()).slice(-2);
  return `${d}${m}${y}`;
};

const getDateRange = (year, month) => {
  const from = new Date(year, month, 1);
  const to = new Date(year, month + 1, 0);
  return { from: toDDMMYY(from), to: toDDMMYY(to) };
};

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const YEARS = Array.from({ length: new Date().getFullYear() - 2017 }, (_, i) => 2018 + i).reverse();

const roleColors = {
  Partner: "bg-blue-100 text-blue-700",
  Client: "bg-emerald-100 text-emerald-700",
  Agent: "bg-purple-100 text-purple-700",
  Owner: "bg-amber-100 text-amber-700",
  Unknown: "bg-slate-100 text-slate-600"
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl border border-slate-700">
      <p className="font-semibold mb-1">{d.period || d.code}</p>
      {d.parcels !== undefined && <p className="text-blue-300">{d.parcels} parcels</p>}
      {d.total !== undefined && <p className="text-emerald-300">{fmt(d.total)}</p>}
    </div>
  );
};

const GrowthBadge = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-slate-400 text-xs">—</span>;
  const isUp = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${isUp ? "text-green-600" : "text-red-600"}`}>
      {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {Math.abs(value).toFixed(1)}%
    </span>
  );
};

const DeltaCard = ({ label, value, prev, icon: Icon, color }) => {
  const delta = prev && prev > 0 ? ((value - prev) / prev * 100) : null;
  return (
    <Card className={`border-${color}-200 bg-${color}-50`}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-xs text-${color}-600`}>{label}</p>
            <p className={`text-xl font-bold text-${color}-700`}>{typeof value === "number" && label !== "Parcels" ? fmt(value) : (value || 0).toLocaleString()}</p>
            {delta !== null && (
              <div className="mt-0.5"><GrowthBadge value={delta} /></div>
            )}
          </div>
          <Icon className={`w-5 h-5 text-${color}-400`} />
        </div>
      </CardContent>
    </Card>
  );
};

const SalesComparisonReportPage = () => {
  usePageTitle("Sales Comparison Report");
  const { token } = useAuth();

  const now = new Date();
  const [period, setPeriod] = useState("monthly");
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [entityType, setEntityType] = useState("all");
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("total");
  const [sortDir, setSortDir] = useState("desc");
  const [pivotMetric, setPivotMetric] = useState("parcels");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const dateRange = useMemo(() => getDateRange(selectedYear, selectedMonth), [selectedYear, selectedMonth]);
  const compareRange = useMemo(() => {
    if (!compareEnabled) return null;
    let prevMonth = selectedMonth - 1;
    let prevYear = selectedYear;
    if (prevMonth < 0) { prevMonth = 11; prevYear--; }
    return getDateRange(prevYear, prevMonth);
  }, [compareEnabled, selectedYear, selectedMonth]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("period", period);
      params.set("date_from", dateRange.from);
      params.set("date_to", dateRange.to);
      if (entityType !== "all") params.set("entity_type", entityType);
      if (compareRange) {
        params.set("compare_from", compareRange.from);
        params.set("compare_to", compareRange.to);
      }
      const res = await fetch(`${BACKEND_URL}/api/reports/sales-comparison?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed");
      setData(await res.json());
    } catch (e) {
      toast.error("Failed: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [token, period, dateRange, compareRange, entityType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!data?.entities) return [];
    let list = [...data.entities];
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      list = list.filter(e => e.code.toLowerCase().includes(t) || (e.name || "").toLowerCase().includes(t));
    }
    list.sort((a, b) => {
      const va = a[sortField] || 0;
      const vb = b[sortField] || 0;
      return sortDir === "desc" ? vb - va : va - vb;
    });
    return list;
  }, [data, searchTerm, sortField, sortDir]);

  const topChart = useMemo(() => filtered.slice(0, 10), [filtered]);
  const maxTotal = Math.max(...topChart.map(e => e.total), 1);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const SortIcon = ({ field }) => (
    <span className="inline-flex ml-1 opacity-50">
      {sortField === field ? (sortDir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3" />}
    </span>
  );

  const exportToPDF = () => {
    if (!data) return;
    const doc = new jsPDF("landscape");
    const pw = doc.internal.pageSize.getWidth();
    doc.setFontSize(14);
    doc.text(`Sales Comparison — ${MONTHS[selectedMonth]} ${selectedYear}`, pw / 2, 15, { align: "center" });
    doc.setFontSize(8); doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString()} | Entities: ${data.summary.entity_count} | Period: ${period}`, pw / 2, 21, { align: "center" });
    const headers = compareEnabled
      ? [["#", "Code", "Role", "Parcels", "Medicine", "Shipping", "Total", "Share", "Prev Total", "Growth"]]
      : [["#", "Code", "Role", "Parcels", "Medicine", "Shipping", "Total", "Share %"]];
    const rows = filtered.map((e, i) => {
      const base = [i + 1, e.code, e.role, e.parcels, fmt(e.medicine), fmt(e.shipping), fmt(e.total), `${e.share_pct}%`];
      if (compareEnabled) { base.push(fmt(e.prev_total || 0)); base.push(e.growth_pct !== null && e.growth_pct !== undefined ? `${e.growth_pct}%` : "-"); }
      return base;
    });
    doc.autoTable({ startY: 25, head: headers, body: rows, theme: "striped", headStyles: { fillColor: [51, 65, 85], fontSize: 7 }, styles: { fontSize: 6 } });
    doc.save(`sales-comparison-${MONTHS[selectedMonth]}-${selectedYear}.pdf`);
    toast.success("PDF exported");
  };

  const exportToCSV = () => {
    if (!data) return;
    const h = compareEnabled ? "Rank,Code,Name,Role,Parcels,Medicine,Shipping,Total,Share%,PrevTotal,Growth%" : "Rank,Code,Name,Role,Parcels,Medicine,Shipping,Total,Share%";
    let csv = `Sales Comparison — ${MONTHS[selectedMonth]} ${selectedYear}\n${h}\n`;
    filtered.forEach((e, i) => {
      csv += `${i+1},${e.code},"${e.name}",${e.role},${e.parcels},${e.medicine},${e.shipping},${e.total},${e.share_pct}`;
      if (compareEnabled) csv += `,${e.prev_total || 0},${e.growth_pct ?? ""}`;
      csv += "\n";
    });
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `sales-comparison-${MONTHS[selectedMonth]}-${selectedYear}.csv`; a.click();
    toast.success("CSV exported");
  };

  if (loading && !data) return (
    <div className="p-6 space-y-4"><Skeleton className="h-10 w-96" /><div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20" />)}</div><Skeleton className="h-48" /><Skeleton className="h-96" /></div>
  );

  const sm = data?.summary || {};

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-[1400px] mx-auto print:p-2" data-testid="sales-comparison-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 print:hidden">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            Sales Comparison
          </h1>
          <p className="text-sm text-slate-500">Partner / Client / Agent wise performance analysis</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline" size="sm"><Download className="w-4 h-4 mr-1" />Export<ChevronDown className="w-3 h-3 ml-1" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={exportToPDF}><FileDown className="w-4 h-4 mr-2" />PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToCSV}><FileSpreadsheet className="w-4 h-4 mr-2" />CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.print()}><Printer className="w-4 h-4 mr-2" />Print</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filter Bar */}
      <Card className="print:hidden">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Period */}
            <div className="flex rounded-lg border border-slate-200 overflow-hidden">
              {["daily", "weekly", "monthly", "yearly"].map(p => (
                <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${period === p ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} data-testid={`period-${p}`}>
                  {p}
                </button>
              ))}
            </div>

            {/* Year / Month */}
            <Select value={String(selectedYear)} onValueChange={v => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-24 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(selectedMonth)} onValueChange={v => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-24 h-8 text-xs"><SelectValue>{MONTHS[selectedMonth]}</SelectValue></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i)}>{m}</SelectItem>)}</SelectContent>
            </Select>

            {/* Entity Type */}
            <Select value={entityType} onValueChange={setEntityType}>
              <SelectTrigger className="w-28 h-8 text-xs"><Filter className="w-3 h-3 mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="partner">Partners</SelectItem>
                <SelectItem value="client">Clients</SelectItem>
                <SelectItem value="agent">Agents</SelectItem>
              </SelectContent>
            </Select>

            {/* Compare Toggle */}
            <div className="flex items-center gap-2 ml-2 border-l pl-3 border-slate-200">
              <Switch id="compare" checked={compareEnabled} onCheckedChange={setCompareEnabled} data-testid="compare-toggle" />
              <Label htmlFor="compare" className="text-xs text-slate-600 cursor-pointer flex items-center gap-1">
                <GitCompare className="w-3 h-3" />vs Prev Month
              </Label>
            </div>

            {/* Search */}
            <div className="relative ml-auto">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-slate-400" />
              <Input className="pl-8 h-8 w-40 text-xs" placeholder="Search code..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="entity-search" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <DeltaCard label="Parcels" value={sm.total_parcels} prev={sm.prev?.parcels} icon={Package} color="blue" />
        <DeltaCard label="Medicine" value={sm.total_medicine} prev={sm.prev?.medicine} icon={DollarSign} color="emerald" />
        <DeltaCard label="Shipping" value={sm.total_shipping} prev={sm.prev?.shipping} icon={Truck} color="amber" />
        <DeltaCard label="Total Cost" value={sm.total_cost} prev={sm.prev?.total} icon={TrendingUp} color="violet" />
      </div>

      {/* Top 10 Bar Chart */}
      {topChart.length > 0 && (
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-600" />Top {Math.min(10, topChart.length)} by Total Cost</CardTitle>
          </CardHeader>
          <CardContent className="px-3 py-2">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={topChart} margin={{ top: 4, right: 4, left: -10, bottom: 0 }} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 9, fill: "#94a3b8" }} tickFormatter={v => fmt(v)} />
                <YAxis type="category" dataKey="code" tick={{ fontSize: 10, fill: "#475569" }} width={70} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                  {topChart.map((entry, idx) => (
                    <Cell key={idx} fill={`rgba(59,130,246,${0.4 + (entry.total / maxTotal) * 0.6})`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Trend Chart */}
      {data?.trend?.length > 1 && (
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-600" />{period.charAt(0).toUpperCase() + period.slice(1)} Trend</CardTitle>
          </CardHeader>
          <CardContent className="px-3 py-2">
            <ResponsiveContainer width="100%" height={120}>
              <LineChart data={data.trend} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="period" tick={{ fontSize: 9, fill: "#94a3b8" }} interval={data.trend.length > 15 ? Math.floor(data.trend.length / 10) : 0} />
                <YAxis tick={{ fontSize: 9, fill: "#94a3b8" }} tickFormatter={v => fmt(v)} />
                <Tooltip content={<ChartTooltip />} />
                <Line type="monotone" dataKey="total" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Period Comparison Table (Pivot) */}
      {data?.pivot?.periods?.length > 0 && (
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="flex items-center gap-2"><GitCompare className="w-4 h-4 text-blue-600" />{period.charAt(0).toUpperCase() + period.slice(1)} Comparison ({data.pivot.entities.length} entities)</span>
              <div className="flex items-center gap-2">
                <div className="flex rounded border border-slate-200 overflow-hidden">
                  <button onClick={() => setPivotMetric("parcels")} className={`px-2 py-1 text-[10px] font-medium ${pivotMetric === "parcels" ? "bg-blue-600 text-white" : "bg-white text-slate-600"}`} data-testid="pivot-parcels-btn">Parcels</button>
                  <button onClick={() => setPivotMetric("total")} className={`px-2 py-1 text-[10px] font-medium ${pivotMetric === "total" ? "bg-blue-600 text-white" : "bg-white text-slate-600"}`} data-testid="pivot-cost-btn">Cost</button>
                </div>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pivot-table">
                <thead className="bg-slate-100 text-slate-600 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium sticky left-0 bg-slate-100 z-10 min-w-[80px]">Code</th>
                    <th className="px-2 py-2 text-left font-medium min-w-[60px]">Role</th>
                    {data.pivot.periods.map(p => (
                      <th key={p} className="px-2 py-2 text-right font-medium min-w-[90px] whitespace-nowrap">{p}</th>
                    ))}
                    <th className="px-2 py-2 text-right font-medium bg-slate-200 min-w-[80px]">Total</th>
                    <th className="px-2 py-2 text-right font-medium bg-slate-200 min-w-[60px]">Last</th>
                  </tr>
                </thead>
                <tbody>
                  {(searchTerm
                    ? data.pivot.entities.filter(e => e.code.toLowerCase().includes(searchTerm.toLowerCase()) || (e.name||"").toLowerCase().includes(searchTerm.toLowerCase()))
                    : data.pivot.entities
                  ).map((e) => {
                    const lastP = e.periods[e.periods.length - 1];
                    const prevP = e.periods.length > 1 ? e.periods[e.periods.length - 2] : null;
                    const lastGrowth = lastP?.growth_pct;
                    return (
                      <tr key={e.code} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`pivot-row-${e.code}`}>
                        <td className="px-2 py-1.5 font-medium text-slate-800 sticky left-0 bg-white z-10">{e.code}</td>
                        <td className="px-2 py-1.5"><Badge className={`text-[10px] ${roleColors[e.role] || roleColors.Unknown}`}>{e.role}</Badge></td>
                        {e.periods.map((p, pi) => {
                          const val = pivotMetric === "parcels" ? p.parcels : p.total;
                          const growth = p.growth_pct;
                          return (
                            <td key={p.period} className="px-2 py-1.5 text-right">
                              <div className="font-mono text-xs">{pivotMetric === "parcels" ? val : fmt(val)}</div>
                              {pi > 0 && growth !== null && (
                                <div className={`text-[10px] font-medium ${growth >= 0 ? "text-green-600" : "text-red-500"}`}>
                                  {growth >= 0 ? "▲" : "▼"}{Math.abs(growth) > 999 ? ">999" : Math.abs(growth).toFixed(0)}%
                                </div>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-2 py-1.5 text-right font-mono font-medium bg-slate-50">
                          {pivotMetric === "parcels" ? e.grand_parcels.toLocaleString() : fmt(e.grand_total)}
                        </td>
                        <td className="px-2 py-1.5 text-right bg-slate-50">
                          {lastGrowth !== null && lastGrowth !== undefined ? (
                            <span className={`text-xs font-medium ${lastGrowth >= 0 ? "text-green-600" : "text-red-500"}`}>
                              {lastGrowth >= 0 ? "▲" : "▼"}{Math.abs(lastGrowth) > 999 ? ">999" : Math.abs(lastGrowth).toFixed(0)}%
                            </span>
                          ) : <span className="text-slate-400 text-xs">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entity Summary Table */}
      <Card>
        <CardHeader className="pb-1">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="flex items-center gap-2"><BarChart3 className="w-4 h-4 text-slate-600" />Entity Summary ({filtered.length})</span>
            {compareEnabled && <Badge className="bg-blue-100 text-blue-700">Comparing vs previous month</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="comparison-table">
              <thead className="bg-slate-100 text-slate-600 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium w-8">#</th>
                  <th className="px-3 py-2 text-left font-medium cursor-pointer hover:text-blue-600" onClick={() => toggleSort("code")}>Code<SortIcon field="code" /></th>
                  <th className="px-3 py-2 text-left font-medium hidden lg:table-cell">Role</th>
                  <th className="px-3 py-2 text-right font-medium cursor-pointer hover:text-blue-600" onClick={() => toggleSort("parcels")}>Parcels<SortIcon field="parcels" /></th>
                  <th className="px-3 py-2 text-right font-medium cursor-pointer hover:text-blue-600 hidden md:table-cell" onClick={() => toggleSort("medicine")}>Medicine<SortIcon field="medicine" /></th>
                  <th className="px-3 py-2 text-right font-medium cursor-pointer hover:text-blue-600 hidden md:table-cell" onClick={() => toggleSort("shipping")}>Shipping<SortIcon field="shipping" /></th>
                  <th className="px-3 py-2 text-right font-medium cursor-pointer hover:text-blue-600" onClick={() => toggleSort("total")}>Total<SortIcon field="total" /></th>
                  <th className="px-3 py-2 text-right font-medium hidden lg:table-cell">Share</th>
                  {compareEnabled && <>
                    <th className="px-3 py-2 text-right font-medium bg-blue-50 hidden md:table-cell">Prev Total</th>
                    <th className="px-3 py-2 text-right font-medium bg-blue-50">Growth</th>
                  </>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((e, i) => (
                  <tr key={e.code} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`entity-row-${e.code}`}>
                    <td className="px-3 py-2 text-slate-400 text-xs">{i + 1}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">{e.code}</td>
                    <td className="px-3 py-2 hidden lg:table-cell"><Badge className={`text-[10px] ${roleColors[e.role] || roleColors.Unknown}`}>{e.role}</Badge></td>
                    <td className="px-3 py-2 text-right font-mono">{e.parcels.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs hidden md:table-cell">{fmt(e.medicine)}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs hidden md:table-cell">{fmt(e.shipping)}</td>
                    <td className="px-3 py-2 text-right font-mono font-medium">{fmt(e.total)}</td>
                    <td className="px-3 py-2 text-right hidden lg:table-cell">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-14 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${e.share_pct}%` }} />
                        </div>
                        <span className="text-xs text-slate-500 w-10 text-right">{e.share_pct}%</span>
                      </div>
                    </td>
                    {compareEnabled && <>
                      <td className="px-3 py-2 text-right font-mono text-xs bg-blue-50/50 hidden md:table-cell">{fmt(e.prev_total || 0)}</td>
                      <td className="px-3 py-2 text-right bg-blue-50/50"><GrowthBadge value={e.growth_pct} /></td>
                    </>}
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={compareEnabled ? 10 : 8} className="px-3 py-8 text-center text-slate-400">No data for this period</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesComparisonReportPage;
